import React from "react";

export type ColorTheme = "blue" | "green" | "orange" | "red";

export const COLOR_THEMES: { value: ColorTheme; label: string; color: string }[] = [
  { value: "blue", label: "Blue", color: "#4a90d9" },
  { value: "green", label: "Green", color: "#4caf50" },
  { value: "orange", label: "Orange", color: "#ff9800" },
  { value: "red", label: "Red", color: "#f44336" },
];

interface ColorThemeSelectorProps {
  currentTheme: ColorTheme;
  onChange: (theme: ColorTheme) => void;
}

export const ColorThemeSelector: React.FC<ColorThemeSelectorProps> = ({
  currentTheme,
  onChange,
}) => {
  return (
    <div
      style={{
        display: "flex",
        gap: "0.5rem",
        padding: "0.5rem",
        alignItems: "center",
      }}
    >
      <span style={{ fontSize: "0.875rem", marginRight: "0.5rem" }}>
        Theme Color:
      </span>
      {COLOR_THEMES.map((theme) => (
        <button
          key={theme.value}
          onClick={() => onChange(theme.value)}
          title={theme.label}
          style={{
            width: "24px",
            height: "24px",
            borderRadius: "50%",
            backgroundColor: theme.color,
            border: currentTheme === theme.value ? "3px solid #333" : "2px solid #ccc",
            cursor: "pointer",
            padding: 0,
            boxShadow: currentTheme === theme.value ? "0 0 4px rgba(0,0,0,0.3)" : "none",
          }}
          aria-label={`${theme.label} theme`}
        />
      ))}
    </div>
  );
};

export const STORAGE_KEY_COLOR_THEME = "whiteboard-color-theme";

export const getStoredColorTheme = (): ColorTheme => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_COLOR_THEME);
    if (stored && COLOR_THEMES.some((t) => t.value === stored)) {
      return stored as ColorTheme;
    }
  } catch (e) {
    // ignore
  }
  return "green";
};

export const setStoredColorTheme = (theme: ColorTheme): void => {
  try {
    localStorage.setItem(STORAGE_KEY_COLOR_THEME, theme);
  } catch (e) {
    // ignore
  }
};

export const applyColorTheme = (theme: ColorTheme): void => {
  const apply = () => {
    const excalidrawEl = document.querySelector(".excalidraw");
    if (excalidrawEl) {
      // Remove all color theme classes
      COLOR_THEMES.forEach((t) => {
        excalidrawEl.classList.remove(`color-theme--${t.value}`);
      });
      // Add the new color theme class
      excalidrawEl.classList.add(`color-theme--${theme}`);
      return true;
    }
    return false;
  };

  // Try immediately
  if (!apply()) {
    // If element not ready, retry after a short delay
    const interval = setInterval(() => {
      if (apply()) {
        clearInterval(interval);
      }
    }, 100);
    // Stop trying after 5 seconds
    setTimeout(() => clearInterval(interval), 5000);
  }
};
